import { ReactNode } from "react";
import ApiGet from "../../../../../api-controller-mod/api-serv/get-api/getApi";
import { API_ENDPOINTS } from "../../../../../context-controller-mod/context-serv/global-provider/api-shell/api-provider";
import ApiDelete from "../../../../../api-controller-mod/api-serv/delete-api/deleteApi";
import { BadgeVariant } from "../../../../../component-controller-mod/component-serv/badge-fs-cp/dynamic-badge-op/ts/statusBadge";
import ApiPost from "../../../../../api-controller-mod/api-serv/post-api/postApi";

export type ApiListResponse<T> = {
  success: boolean;
  requestId: string;
  data: {
    items: T[];
    pagination: InboxPagination;
  };
};

export type ApiSingleResponse<T> = {
  success: boolean;
  requestId: string;
  data: T;
};

export type CareerSubmitFilters = {
  department?: number;
  level?: number;
  jobType?: number;
  search?: string;
};

export const getCareerSubmitApi = (page = 1, size = 10, filters?: CareerSubmitFilters) =>
  ApiGet<ApiListResponse<CareerApplicationItem>>(API_ENDPOINTS.GET_CAREER_SUBMIT_INDEX, {
    params: {
      page,
      size,
      ...(filters?.department ? { department: filters.department } : {}),
      ...(filters?.level ? { jobLevel: filters.level } : {}),
      ...(filters?.jobType ? { jobType: filters.jobType } : {}),
      ...(filters?.search ? { search: filters.search } : {}), 
    },
  });

export type CareerApplicationItem = {
  id: number;
  applicationId: string;
  candidateName: string;
  jobCode: string;
  jobTitle: string;
  status: string;
  assignedRecruiterId?: string | null;
  isRead: boolean;
  appliedAt: string;
  createdAt: string;
  updatedAt: string;
};

export const columns = [
  'ID',
  'Candidate name',
  'Job',
  'Experience',
  'Applied date',
  'Status',
  'Action'
];

export type InboxRow = {
  id: string;
  ID: string;
  "Candidate name": string;
  Job: string;
  Experience: string;  
  "Applied date": string;
  Status: string;
  Action?: ReactNode;
};

export const status = [
  { value: 'all', label: 'All' },
  { value: 'read', label: 'Read' },
  { value: 'new', label: 'New' },
];

export const TypeBussiness = [
  { value: 'all', label: 'All' },
  { value: 'business', label: 'Business' },
  { value: 'sale', label: 'Sale' },
];

export type InboxItem = {
  id?: string | number;
  contactId?: string | number;
  fullName?: string;
  name?: string;
  categoryName?: string;
  contactType?: string;
  email?: string;
  phone?: string;
  phoneNumber?: string;
  status?: string;
};

export type InboxPagination = {
  page: number;
  size: number;
  totalRecords: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
};

export type InboxApiResponse = {
  data?: {
    items?: InboxItem[];
    pagination?: InboxPagination;
  };
};

export type InboxAttachment = {
  id: string;
  filename: string;
  mimeType?: string;
  fileSize?: number; 
  url?: string;
  downloadUrl?: string | null;
};

export type InboxDetail = {
  id: string;               
  fullName?: string;
  email?: string;
  phone?: string;
  company?: string;
  subject?: string;
  message?: string;
  status?: string;            
  categoryName?: string;     
  createdAt?: string;
  updatedAt?: string;
  attachments?: InboxAttachment[];
};

export type InboxDetailApiResponse = {
  data?: InboxDetail;
};

export type CareerAttachment = {
  attachmentId: string;
  fileName: string;
  fileSize: number; 
  mimeType?: string;
  url?: string;
};

export type CareerPreview = {
  applicationId: string;
  candidateName: string;
  email: string;
  country?: string | null;
  phone?: string | null;
  jobCode: string;
  jobTitle: string;
  jobLevel?: string | null;
  departmentName?: string | null;
  appliedAt: string;
  status: "new" | "shortlisted" | "interviewed" | "rejected" | "reviewed";
  assignedRecruiterId?: string | null;
  attachments?: CareerAttachment;
};

export const getCareerSubmitPreviewApi = (applicationId: string) =>
  ApiGet<ApiSingleResponse<CareerPreview>>(API_ENDPOINTS.GET_CAREER_SUBMIT_PREVIEW, {
    params: { applicationId },
  });

export const getInboxDetailApi = (inboxUuid: string) =>
  ApiGet<InboxDetailApiResponse>(API_ENDPOINTS.GET_INBOX_DETAIL, {
    params: { inboxUuid },
  });

export const deleteInboxItem = (uuid: string) =>
  ApiDelete(API_ENDPOINTS.CAREER_SUBMITTED_DELETE, {
    params: { uuid },
  });

export const toBadgeVariant = (value?: string): BadgeVariant => {
  switch (value?.toLowerCase()) {
    case "published":
      return "published";
    case "unpublished":
    case "unpublish":
      return "unpublished";
    case "draft":
      return "draft";
    case "new":
      return "new";
    case "reviewed":
      return "reviewed";
    case "shortlisted":
      return "shortlisted";
    case "interviewed":
      return "interviewed";
    case "rejected":
      return "rejected";
    case "hired":
      return "hired";
    default:
      return "draft";
  }
};

export const formatBytes = (bytes?: number) => {
  if (bytes == null) return "-";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

export const normalizeSearch = (s: string) =>
  s.replace(/\u00A0/g, " ").replace(/\s+/g, " ").trim();

export type DepartmentItem = {
  id: number;
  departmentUuid: string;
  name: string;
  code: string;
  slug: string;
  description?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export const getDepartmentIndexApi = () =>
  ApiGet<ApiListResponse<DepartmentItem>>(API_ENDPOINTS.GET_JOB_DEPARTMENT);

export type JobLevelItem = {
  id: number;
  uuid: string;
  name: string;
  code: string;
  rank: number;
  description?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

// because response is data.data.items
export type JobLevelIndexResponse = {
  success: boolean;
  requestId: string;
  data: {
    success: boolean;
    data: {
      items: JobLevelItem[];
    };
  };
};

export const getJobLevelIndexApi = () =>
  ApiGet<JobLevelIndexResponse>(API_ENDPOINTS.GET_JOB_LEVEL);

export type JobTypeItem = {
  id: number;
  uuid: string;
  name: string;
  code: string;
  description?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type ApiActionResponse = {
  success: boolean;
  requestId: string;
  data?: unknown;
};

export const getJobTypeIndexApi = () =>
  ApiGet<ApiListResponse<JobTypeItem>>(API_ENDPOINTS.GET_JOB_TYPE);

export type CareerSubmitStatusAction = "review" | "shortlisted" | "rejected" | "hired";

export const updateCareerApplicationStatusApi = (
  applicationId: string,
  action: CareerSubmitStatusAction
) => {
  const endpoint =
    action === "review"
      ? API_ENDPOINTS.CAREER_APP_REVIEW
      : action === "shortlisted"
        ? API_ENDPOINTS.CAREER_APP_SHORTLISTED
        : action === "rejected"
          ? API_ENDPOINTS.CAREER_APP_REJECT
          : API_ENDPOINTS.CAREER_APP_HIRE;

  const qs = `?applicationId=${encodeURIComponent(applicationId)}`;

  return ApiPost<ApiActionResponse>(`${endpoint}${qs}`);
};

export const STATUS_OPTIONS = [
  { value: "review", label: "Reviewed" },
  { value: "shortlisted", label: "Shortlisted" },
  { value: "rejected", label: "Rejected" },
  { value: "hired", label: "Hired" },
];

export const batchDeleteCareerApplications = (ids: string[]) =>
  ApiDelete(API_ENDPOINTS.CAREER_SUBMITTED_BATCH_DELETE, {
    data: { ids }, // IMPORTANT: must be inside data
  });